import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2, Layers } from 'lucide-react';
import { Input } from '@/shared/components/ui/input';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useProjectFields } from '../hooks/useProjectFields';
import { useDeleteProjectField } from '../hooks/useProjectFieldsMutations';
import { resolveFieldType, FIELD_REGISTRY } from '../registry/field-registry';
import type { ProjectField } from '../types';

interface ExistingFieldsListProps {
  projectId: string;
  onEdit: (field: ProjectField) => void;
}

export function ExistingFieldsList({ projectId, onEdit }: ExistingFieldsListProps) {
  const { t } = useTranslation('customFields');
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const { data, isLoading, isError, refetch } = useProjectFields(projectId, {
    searchTerm: debouncedSearch || undefined,
  });

  const deleteMutation = useDeleteProjectField(projectId);

  const handleDelete = async (field: ProjectField) => {
    if (!field.id) return;
    const { confirmed } = await confirm({
      title: t('delete.confirmTitle'),
      description: t('delete.confirmDescription'),
    });
    if (!confirmed) return;
    deleteMutation.mutate(field.id);
  };

  const fields = data?.items ?? [];

  return (
    <>
      <div className="flex flex-col gap-3 p-3">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('existingFields.search')}
          className="h-8 text-sm"
          data-testid="custom-fields-search-input"
        />

        <div aria-hidden="true">
          {isLoading && (
            <div data-testid="custom-fields-loading-skeleton" className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-9 w-full rounded" />
              ))}
            </div>
          )}
        </div>

        {isError && !isLoading && (
          <div
            data-testid="custom-fields-error-message"
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm"
          >
            <p className="text-red-800">{t('existingFields.error')}</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={() => refetch()}>
              {t('existingFields.retry')}
            </Button>
          </div>
        )}

        {!isLoading && !isError && (
          <>
            {fields.length === 0 ? (
              <div
                data-testid="custom-fields-empty-state"
                className="flex flex-col items-center py-8 text-center"
              >
                <Layers className="h-8 w-8 text-muted-foreground mb-3" aria-hidden="true" />
                <p className="text-sm font-medium">{t('existingFields.empty')}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('existingFields.emptyDescription')}
                </p>
              </div>
            ) : (
              <div data-testid="custom-fields-existing-list" className="space-y-0.5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-1 mb-1">
                  {t('existingFields.created')}
                </p>
                {fields.map((field) => (
                  <div
                    key={field.id}
                    data-testid={`custom-fields-existing-item-${field.id}`}
                    className="group flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-accent min-h-9"
                  >
                    {(() => {
                      const ft = resolveFieldType(field.fieldType);
                      const Icon = ft != null ? FIELD_REGISTRY[ft]?.icon : undefined;
                      return Icon ? (
                        <Icon
                          className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                          aria-hidden="true"
                        />
                      ) : null;
                    })()}
                    <span className="flex-1 text-sm truncate">{field.name}</span>
                    <div className="hidden group-hover:flex items-center gap-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => onEdit(field)}
                            data-testid={`custom-fields-edit-button-${field.id}`}
                            aria-label={t('actions.edit')}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t('actions.edit')}</TooltipContent>
                      </Tooltip>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => handleDelete(field)}
                            disabled={deleteMutation.isPending}
                            data-testid={`custom-fields-delete-button-${field.id}`}
                            aria-label={t('actions.delete')}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t('actions.delete')}</TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {isOpen && options && (
        <ConfirmDialog
          open={isOpen}
          onOpenChange={(open) => !open && handleCancel()}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </>
  );
}
